import { useTranslation } from 'i18n';
import { formatNumber, Stack } from 'ui';

import { formatPercentageFeature } from '../../utils/formatFeature/formatFeature';
import { MaterialBreakdownItem } from '../MaterialBreakdownItem/MaterialBreakdownItem';
import { MaterialSampleBreakdownItem } from '../MaterialSampleBreakdownItem/MaterialSampleBreakdownItem';

const ALUMINIUM_KEYS: string[] = [
  'Aluminium - Taint Tabor',
  'Aluminium - cast',
  'Aluminium - radiator',
  'Aluminium - foil/UBC',
];

const ALU_WITH_ATTACHMENTS_KEYS: string[] = [
  'Aluminium with non-metal attachments',
  'Aluminium with iron attachments',
  'Aluminium - cast with iron attachments',
];

const NON_ALUMINIUM_KEYS: string[] = [
  'Other Metal',
  'Ferrous metal',
  'Non-Metal',
  'Prohibitive',
  'Fines <20mm',
  'Stainless steel',
];

export const TaintTaborMaterialSampleBreakdown = ({
  totalWeight,
  breakdown,
  precision = 1,
}: {
  totalWeight: number;
  breakdown: Record<string, number>;
  precision?: number;
}) => {
  const { t } = useTranslation('features');
  const totalAluminium = ALUMINIUM_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);
  const totalAluWithAttachments = ALU_WITH_ATTACHMENTS_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);
  const totalNonAluminium = NON_ALUMINIUM_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <div>
        <MaterialBreakdownItem
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.aluminium')}
          variant="bold"
          value={formatNumber(totalAluminium, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={totalAluminium ? formatPercentageFeature(totalAluminium / totalWeight) : '0%'}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Aluminium - Taint Tabor']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.taintTabor')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Aluminium - cast']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.cast')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Aluminium - radiator']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.radiatorCondenser')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Aluminium - foil/UBC']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.ubcFoil')}
          totalWeight={totalWeight}
        />
      </div>
      <div>
        <MaterialBreakdownItem
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.aluWithAttachments')}
          variant="bold"
          value={formatNumber(totalAluWithAttachments, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={
            totalAluWithAttachments ? formatPercentageFeature(totalAluWithAttachments / totalWeight) : '0%'
          }
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Aluminium with non-metal attachments']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.aluNonMetal')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Aluminium with iron attachments']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.aluIron')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Aluminium - cast with iron attachments']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.castIron')}
          totalWeight={totalWeight}
        />
      </div>
      <div>
        <MaterialBreakdownItem
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.nonAluminium')}
          variant="bold"
          value={formatNumber(totalNonAluminium, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={totalNonAluminium ? formatPercentageFeature(totalNonAluminium / totalWeight) : '0%'}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Ferrous metal']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.ferrousMetal')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Stainless steel']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.stainlessSteel')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Other Metal']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.otherMetal')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Non-Metal']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.nonMetal')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Fines <20mm']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.finesLt20mm')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Prohibitive']}
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.prohibitive')}
          totalWeight={totalWeight}
        />
        <MaterialBreakdownItem
          label={t('breakdowns.taintTaborMaterialSampleBreakdown.labels.total')}
          value={formatNumber(totalWeight, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={'100%'}
          variant="bold"
        />
      </div>
    </Stack>
  );
};
